import type { IAgentFeatured, IHowItWorksStep } from "@/api/agents";
import { Input } from "@/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTitle,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ArrowLeft, Info, Search, X } from "lucide-react";
import {
    lazy,
    Suspense,
    useEffect,
    useRef,
    useState,
    type ComponentProps,
    type FC,
    type ReactNode,
} from "react";
import type { WorkflowIconProps } from "./WorkflowIcon";

export interface WorkflowPickerProps {
    workflows: IAgentFeatured[];
    selectedWorkflow: IAgentFeatured | null;
    onSelect: (workflow: IAgentFeatured) => void;
    onClear: () => void;
    children?: ReactNode;
}

const hasText = (value: string | null | undefined): value is string =>
    typeof value === "string" && value.trim().length > 0;

const getWorkflowLabel = (workflow: IAgentFeatured): string =>
    hasText(workflow.button_label) ? workflow.button_label : workflow.name;

const LazyWorkflowIcon = lazy<FC<WorkflowIconProps>>(async () => {
    try {
        return await import("./WorkflowIcon");
    } catch {
        return { default: () => null };
    }
});

const WorkflowIcon: FC<
    Omit<WorkflowIconProps, "name"> & {
        name: string | null | undefined;
    }
> = ({ name, ...props }) => {
    if (!hasText(name)) return null;
    return (
        <Suspense fallback={null}>
            <LazyWorkflowIcon name={name} {...props} />
        </Suspense>
    );
};

const WorkflowSteps: FC<
    { steps: IHowItWorksStep[] } & Omit<ComponentProps<"ol">, "children">
> = ({ steps, className, ...props }) => (
    <ol
        role="list"
        className={cn("flex list-none flex-col gap-2.5", className)}
        {...props}
    >
        {steps.map((step, index) => (
            <li
                key={`${step.title}:${index}`}
                className="flex items-start gap-2.5"
            >
                <span
                    className="bg-secondary text-primary mt-px flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                    aria-hidden="true"
                >
                    {index + 1}
                </span>
                <span className="flex min-w-0 flex-col gap-0.5 break-words">
                    <span className="text-[13px] leading-snug font-semibold">
                        {step.title}
                    </span>
                    <span className="text-muted-foreground text-[12.5px] leading-[1.45]">
                        {step.description}
                    </span>
                </span>
            </li>
        ))}
    </ol>
);

const WorkflowSummary: FC<{ workflow: IAgentFeatured }> = ({ workflow }) => (
    <div className="flex min-w-0 items-center gap-3">
        <WorkflowIcon
            name={workflow.icon}
            className="size-4.5"
            containerClassName="bg-secondary text-primary flex size-9 shrink-0 items-center justify-center rounded-[9px]"
            aria-hidden="true"
        />
        <span className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-[15px] leading-tight font-semibold">
                {workflow.name}
            </span>
            {hasText(workflow.description) && (
                <span className="text-muted-foreground text-[12.5px] leading-snug break-words">
                    {workflow.description}
                </span>
            )}
        </span>
    </div>
);

const WorkflowPreview: FC<{ workflow: IAgentFeatured }> = ({ workflow }) => {
    const steps = workflow.how_it_works ?? [];
    const hasSteps = steps.length > 0;

    return (
        <div className="flex min-h-0 w-full flex-col">
            <div className={cn("px-4 pt-3.5", !hasSteps && "pb-3.5")}>
                <WorkflowSummary workflow={workflow} />
            </div>
            {hasSteps && (
                <>
                    <p className="text-muted-foreground mt-3 px-4 text-[11px] font-semibold tracking-[0.08em] uppercase">
                        What this workflow does
                    </p>
                    <WorkflowSteps
                        steps={steps}
                        className="max-h-[250px] min-h-0 overflow-y-auto overscroll-contain px-4 pt-2 pb-3"
                    />
                </>
            )}
            <div className="border-border text-muted-foreground w-full border-t px-4 py-2.5 text-xs">
                Click to use this workflow
            </div>
        </div>
    );
};

const PrimaryWorkflowButton: FC<{
    workflow: IAgentFeatured;
    selected: boolean;
    onSelect: (workflow: IAgentFeatured) => void;
}> = ({ workflow, selected, onSelect }) => {
    const label = getWorkflowLabel(workflow);
    const trigger = (
        <button
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(workflow)}
            className="bg-primary text-primary-foreground focus-visible:ring-ring inline-flex min-h-9 max-w-full items-center gap-2 rounded-full border border-transparent px-4 py-2 text-[13.5px] font-medium whitespace-nowrap transition-[filter,transform] outline-none hover:brightness-95 focus-visible:ring-2 focus-visible:ring-offset-2 active:translate-y-px motion-reduce:transition-none motion-reduce:active:translate-y-0"
        >
            <WorkflowIcon
                name={workflow.icon}
                className="size-4"
                aria-hidden="true"
            />
            <span className="truncate">{label}</span>
        </button>
    );

    if (!hasText(workflow.description) && !workflow.how_it_works?.length) {
        return trigger;
    }

    return (
        <Tooltip>
            <TooltipTrigger render={trigger} />
            <TooltipContent
                variant="rich"
                side="top"
                sideOffset={10}
                showArrow={false}
                className="max-h-(--available-height) w-[420px] max-w-[calc(100vw-2rem)] gap-0 overflow-y-auto overscroll-contain rounded-[14px] p-0 motion-reduce:animate-none"
            >
                <WorkflowPreview workflow={workflow} />
            </TooltipContent>
        </Tooltip>
    );
};

const WorkflowList: FC<{
    workflows: IAgentFeatured[];
    query: string;
    onQueryChange: (query: string) => void;
    onSelect: (workflow: IAgentFeatured) => void;
    onInspect: (workflow: IAgentFeatured) => void;
    restoreFocusToWorkflowId: number | null;
    onFocusRestored: () => void;
}> = ({
    workflows,
    query,
    onQueryChange,
    onSelect,
    onInspect,
    restoreFocusToWorkflowId,
    onFocusRestored,
}) => {
    const restoredRowRef = useRef<HTMLButtonElement>(null);
    const showSearch = workflows.length >= 10;
    const normalizedQuery = showSearch ? query.trim().toLowerCase() : "";
    const filteredWorkflows = normalizedQuery
        ? workflows.filter((workflow) =>
              [workflow.name, workflow.button_label, workflow.description].some(
                  (value) =>
                      hasText(value) &&
                      value.toLowerCase().includes(normalizedQuery)
              )
          )
        : workflows;

    useEffect(() => {
        if (restoreFocusToWorkflowId === null) return;
        if (!restoredRowRef.current && query) {
            onQueryChange("");
            return;
        }
        restoredRowRef.current?.focus({ preventScroll: true });
        onFocusRestored();
    }, [onFocusRestored, onQueryChange, query, restoreFocusToWorkflowId]);

    return (
        <div className="flex min-h-0 flex-col">
            {showSearch && (
                <div className="border-border shrink-0 border-b p-3">
                    <div className="relative">
                        <Search
                            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2"
                            aria-hidden="true"
                        />
                        <Input
                            value={query}
                            onChange={(event) =>
                                onQueryChange(event.target.value)
                            }
                            placeholder="Search workflows"
                            aria-label="Search workflows"
                            className="bg-muted/30 h-8 border-0 pl-8 text-[13px] shadow-none focus-visible:ring-2"
                        />
                    </div>
                </div>
            )}
            <div className="flex max-h-[308px] min-h-0 flex-col overflow-y-auto overscroll-contain p-1.5">
                {filteredWorkflows.map((workflow) => (
                    <div
                        key={workflow.id}
                        className="hover:bg-muted/30 focus-within:bg-muted/30 flex items-center rounded-[10px]"
                    >
                        <button
                            ref={
                                workflow.id === restoreFocusToWorkflowId
                                    ? restoredRowRef
                                    : undefined
                            }
                            type="button"
                            aria-label={`Use ${workflow.name}`}
                            onClick={() => onSelect(workflow)}
                            className="focus-visible:ring-ring flex min-w-0 flex-1 items-center gap-3 rounded-[10px] px-2.5 py-2 text-left outline-none focus-visible:ring-2"
                        >
                            <WorkflowIcon
                                name={workflow.icon}
                                containerClassName="bg-secondary text-primary flex size-8 shrink-0 items-center justify-center rounded-lg"
                                aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-[13.5px] font-medium">
                                    {getWorkflowLabel(workflow)}
                                </span>
                                {hasText(workflow.description) && (
                                    <span className="text-muted-foreground block truncate text-xs">
                                        {workflow.description}
                                    </span>
                                )}
                            </span>
                        </button>
                        {workflow.how_it_works?.length ? (
                            <button
                                type="button"
                                aria-label={`What ${workflow.name} does`}
                                onClick={() => onInspect(workflow)}
                                className="text-muted-foreground hover:bg-secondary hover:text-primary focus-visible:ring-ring mr-2 flex size-7 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2"
                            >
                                <Info className="size-4" aria-hidden="true" />
                            </button>
                        ) : null}
                    </div>
                ))}
                {filteredWorkflows.length === 0 && (
                    <p className="text-muted-foreground px-3 py-5 text-center text-[12.5px]">
                        No workflows match your search
                    </p>
                )}
            </div>
        </div>
    );
};

const WorkflowDetail: FC<{
    workflow: IAgentFeatured;
    onBack: () => void;
    onUse: (workflow: IAgentFeatured) => void;
}> = ({ workflow, onBack, onUse }) => {
    const backButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        backButtonRef.current?.focus({ preventScroll: true });
    }, []);

    return (
        <div className="flex min-h-0 flex-col">
            <div className="shrink-0 px-2.5 pt-2">
                <button
                    ref={backButtonRef}
                    type="button"
                    onClick={onBack}
                    className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 text-[12.5px] font-medium outline-none focus-visible:ring-2"
                >
                    <ArrowLeft className="size-3.5" aria-hidden="true" />
                    All workflows
                </button>
            </div>
            <div className="shrink-0 px-4 pt-2">
                <WorkflowSummary workflow={workflow} />
            </div>
            <p className="text-muted-foreground mt-3.5 shrink-0 px-4 text-[11px] font-semibold tracking-[0.08em] uppercase">
                What this workflow does
            </p>
            <WorkflowSteps
                steps={workflow.how_it_works ?? []}
                aria-label={`${workflow.name} workflow steps`}
                tabIndex={0}
                className="focus-visible:ring-ring max-h-[186px] min-h-0 overflow-y-auto overscroll-contain px-4 pt-2 pb-1 outline-none focus-visible:ring-2 focus-visible:ring-inset"
            />
            <div className="border-border shrink-0 border-t px-4 pt-3 pb-3.5">
                <button
                    type="button"
                    onClick={() => onUse(workflow)}
                    className="bg-primary text-primary-foreground focus-visible:ring-ring min-h-9 w-full rounded-[10px] px-3.5 py-2 text-[13.5px] font-semibold transition-[opacity,transform] outline-none hover:opacity-85 focus-visible:ring-2 focus-visible:ring-offset-2 active:translate-y-px motion-reduce:transition-none motion-reduce:active:translate-y-0"
                >
                    Use this workflow
                </button>
            </div>
        </div>
    );
};

export const WorkflowPicker: FC<WorkflowPickerProps> = ({
    workflows,
    selectedWorkflow,
    onSelect,
    onClear,
    children,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [inspectedWorkflowId, setInspectedWorkflowId] = useState<
        number | null
    >(null);
    const [restoreFocusToWorkflowId, setRestoreFocusToWorkflowId] = useState<
        number | null
    >(null);
    const inspectedWorkflow =
        workflows.find((workflow) => workflow.id === inspectedWorkflowId) ??
        null;

    useEffect(() => {
        if (
            inspectedWorkflowId === null ||
            inspectedWorkflow?.how_it_works?.length
        ) {
            return;
        }
        setInspectedWorkflowId(null);
        if (!inspectedWorkflow) {
            setOpen(false);
            return;
        }
        setRestoreFocusToWorkflowId(inspectedWorkflow.id);
    }, [inspectedWorkflow, inspectedWorkflowId]);

    const handleOpenChangeComplete = (nextOpen: boolean) => {
        if (nextOpen) return;
        setQuery("");
        setInspectedWorkflowId(null);
        setRestoreFocusToWorkflowId(null);
    };

    const handleSelect = (workflow: IAgentFeatured) => {
        onSelect(workflow);
        setOpen(false);
    };

    const handleInspect = (workflow: IAgentFeatured) => {
        setRestoreFocusToWorkflowId(null);
        setInspectedWorkflowId(workflow.id);
    };

    const handleBack = () => {
        setRestoreFocusToWorkflowId(inspectedWorkflowId);
        setInspectedWorkflowId(null);
    };

    return (
        <TooltipProvider delay={450}>
            <div className="@container flex w-full flex-col items-center">
                <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                    {workflows.slice(0, 3).map((workflow) => (
                        <PrimaryWorkflowButton
                            key={workflow.id}
                            workflow={workflow}
                            selected={selectedWorkflow?.id === workflow.id}
                            onSelect={handleSelect}
                        />
                    ))}
                    <Popover
                        open={open}
                        onOpenChange={setOpen}
                        onOpenChangeComplete={handleOpenChangeComplete}
                    >
                        <PopoverTrigger
                            aria-label={`All workflows, ${workflows.length} available`}
                            className={cn(
                                "text-primary-foreground border-border focus-visible:ring-ring bg-copper inline-flex min-h-9 items-center gap-2 rounded-full px-4 py-2 text-[13.5px] font-medium whitespace-nowrap focus-visible:ring-2 focus-visible:ring-offset-2",
                                open && "ring-copper/20 ring-4"
                            )}
                        >
                            <WorkflowIcon
                                name={"workflow"}
                                className="size-4"
                                aria-hidden="true"
                            />
                            All workflows
                            <span className="bg-secondary/20 text-primary-foreground rounded-full px-1.5 py-0.5 text-[11.5px] leading-none font-semibold">
                                {workflows.length}
                            </span>
                        </PopoverTrigger>
                        <PopoverContent
                            keepMounted
                            animated={false}
                            side="top"
                            sideOffset={10}
                            initialFocus={(openType) => openType === "keyboard"}
                            className="max-h-(--available-height) w-[460px] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-[14px] p-0"
                        >
                            <PopoverTitle className="sr-only">
                                {inspectedWorkflow
                                    ? `${inspectedWorkflow.name} workflow details`
                                    : "All workflows"}
                            </PopoverTitle>
                            {inspectedWorkflow ? (
                                <WorkflowDetail
                                    workflow={inspectedWorkflow}
                                    onBack={handleBack}
                                    onUse={handleSelect}
                                />
                            ) : (
                                <WorkflowList
                                    workflows={workflows}
                                    query={query}
                                    onQueryChange={setQuery}
                                    onSelect={handleSelect}
                                    onInspect={handleInspect}
                                    restoreFocusToWorkflowId={
                                        restoreFocusToWorkflowId
                                    }
                                    onFocusRestored={() =>
                                        setRestoreFocusToWorkflowId(null)
                                    }
                                />
                            )}
                        </PopoverContent>
                    </Popover>
                </div>
                {selectedWorkflow && (
                    <div className="mt-4 flex w-full flex-col">
                        <div className="flex min-h-7 items-center gap-2">
                            <WorkflowIcon
                                name={selectedWorkflow.icon}
                                className="text-primary size-4"
                                aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold">
                                {selectedWorkflow.name}
                            </span>
                            <button
                                type="button"
                                onClick={onClear}
                                className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring ml-auto inline-flex min-h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium outline-none focus-visible:ring-2"
                            >
                                <X className="size-3" aria-hidden="true" />
                                Clear
                            </button>
                        </div>
                        {children}
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
};
